import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAppData } from '../../contexts/AppDataContext';
import { useEffect } from 'react';
import { 
  UserGroupIcon,
  UserIcon,
  IdentificationIcon,
  ClipboardDocumentCheckIcon,
  CalendarDaysIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
  PencilIcon,
  TrashIcon,
  EnvelopeIcon,
  PhoneIcon,
  BanknotesIcon,
  LockClosedIcon,
  LockOpenIcon,
  DocumentCheckIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import AttendanceTracker from '../../components/employees/AttendanceTracker';

const EmployeesPage = () => {
  const { t } = useLanguage();
  const { getEmployees, addNotification, selectedEmployee, setSelectedEmployee } = useAppData();
  const [activeTab, setActiveTab] = useState('staff');
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);

  // Récupérer les employés depuis le contexte global
  const mockEmployees = getEmployees();
  
  // Utiliser selectedEmployee du contexte global s'il existe
  useEffect(() => {
    if (selectedEmployee) {
      // Si un employé est sélectionné ailleurs dans l'application, l'afficher automatiquement
      setShowAddForm(false);
    }
  }, [selectedEmployee]);

  // Statistiques du personnel
  const employeeStats = [
    {
      title: 'Total Employés',
      value: mockEmployees.length,
      icon: UserGroupIcon,
      color: 'from-blue-500 to-blue-600',
    },
    {
      title: 'Présents Aujourd\'hui',
      value: Math.floor(mockEmployees.length * 0.85), // Simulation 85% de présence
      icon: ClipboardDocumentCheckIcon,
      color: 'from-green-500 to-green-600',
    },
    {
      title: 'Masse Salariale',
      value: mockEmployees.reduce((sum, employee) => sum + employee.salary, 0).toLocaleString('fr-DZ') + ' DA',
      icon: BanknotesIcon,
      color: 'from-purple-500 to-purple-600',
    },
  ];

  // Départements disponibles
  const departments = [
    { value: 'all', label: 'Tous les départements' },
    { value: 'management', label: 'Direction' },
    { value: 'reception', label: 'Accueil' },
    { value: 'coaching', label: 'Coaching' },
    { value: 'finance', label: 'Finance' },
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'cleaning', label: 'Nettoyage' }
  ];

  // Distribution des employés par département
  const employeeDistribution = departments
    .filter(d => d.value !== 'all')
    .map(department => {
      const employees = mockEmployees.filter(e => e.department === department.value);
      const count = employees.length;
      const percentage = (count / mockEmployees.length * 100).toFixed(1);
      
      return {
        name: department.label,
        count,
        percentage,
        color: department.value === 'management' ? 'from-purple-400 to-purple-500' :
               department.value === 'reception' ? 'from-blue-400 to-blue-500' :
               department.value === 'coaching' ? 'from-green-400 to-green-500' :
               department.value === 'finance' ? 'from-amber-400 to-amber-500' :
               department.value === 'maintenance' ? 'from-red-400 to-red-500' :
               'from-indigo-400 to-indigo-500'
      };
    });

  // Onglets
  const tabs = [
    {
      id: 'staff',
      name: 'Personnel',
      icon: UserGroupIcon,
      description: 'Liste des employés'
    },
    {
      id: 'attendance',
      name: 'Présence',
      icon: ClipboardDocumentCheckIcon,
      description: 'Suivi des présences'
    },
    {
      id: 'schedule',
      name: 'Planning',
      icon: CalendarDaysIcon,
      description: 'Horaires de travail'
    },
    {
      id: 'roles',
      name: 'Rôles & Accès',
      icon: IdentificationIcon,
      description: 'Gestion des droits'
    }
  ];

  // Filtrage des employés
  const filteredEmployees = mockEmployees
    .filter(employee => 
      `${employee.firstName} ${employee.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.position.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.email.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter(employee => departmentFilter === 'all' || employee.department === departmentFilter);

  // Gestion de l'ajout/modification d'employé avec notification
  const handleSaveEmployee = (employeeData) => {
    console.log('Sauvegarde de l\'employé:', employeeData);
    
    // Notifier le succès
    addNotification({
      type: 'success',
      title: 'Employé sauvegardé',
      message: `Les informations de ${employeeData.firstName || 'l\'employé'} ont été mises à jour`,
      link: '/employees'
    });
    
    setShowAddForm(false);
  };

  // Gestion de la suppression d'employé avec notification
  const handleDeleteEmployee = (id) => {
    console.log('Suppression de l\'employé avec ID:', id);
    
    // Notifier la suppression
    addNotification({
      type: 'info',
      title: 'Employé supprimé',
      message: `L'employé a été supprimé avec succès`,
      link: '/employees'
    });
    
    if (selectedEmployee && selectedEmployee.id === id) {
      setSelectedEmployee(null);
    }
    
    setIsDeleteModalOpen(false);
    setEmployeeToDelete(null);
  };

  // Fonction pour ouvrir la modal de suppression
  const openDeleteModal = (employee) => {
    setEmployeeToDelete(employee);
    setIsDeleteModalOpen(true);
  };

  // Fonction pour modifier un employé
  const editEmployee = (employee) => {
    setSelectedEmployee(employee);
    setShowAddForm(true);
  };

  // Fonction pour voir le détail d'un employé
  const viewEmployee = (employee) => {
    setSelectedEmployee(employee);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white">
            Gestion du Personnel
          </h1>
          <p className="text-white/60 mt-2">
            Gérez vos employés et le planning
          </p>
        </div>
        <button
          onClick={() => {
            setSelectedEmployee(null);
            setShowAddForm(true);
          }}
          className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 flex items-center space-x-2"
        >
          <PlusIcon className="h-5 w-5" />
          <span>Nouvel Employé</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {employeeStats.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/60 text-sm font-medium">{stat.title}</p>
                <p className="text-2xl font-bold text-white mt-2">{stat.value}</p>
              </div>
              <div className={`p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
        <div className="flex space-x-1 overflow-x-auto hide-scrollbar">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                  : 'text-white/80 hover:bg-white/10 hover:text-white'
              }`}
            >
              <tab.icon className="h-5 w-5" />
              <span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Répartition par département */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
      >
        <h3 className="text-xl font-semibold text-white mb-4">
          Répartition par Département
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
          {employeeDistribution.map((item) => (
            <div 
              key={item.name}
              className="bg-white/5 rounded-xl p-4 border border-white/10"
            >
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-medium text-white">{item.name}</h4>
                <div className={`text-sm rounded-full px-2 py-0.5 bg-gradient-to-r ${item.color} text-white`}>
                  {item.count}
                </div>
              </div>
              <div className="w-full bg-white/10 h-3 rounded-full">
                <div 
                  className={`h-3 rounded-full bg-gradient-to-r ${item.color}`}
                  style={{ width: `${item.percentage}%` }}
                ></div>
              </div>
              <p className="mt-2 text-sm text-white/60">
                {item.percentage}% des employés
              </p>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Content based on active tab */}
      {activeTab === 'staff' && (
        <div className="space-y-6">
          {/* Search and Filter Bar */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <MagnifyingGlassIcon className="absolute left-3 top-3 h-5 w-5 text-white/50" />
                <input
                  type="text"
                  placeholder="Rechercher un employé..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Department Filter */}
              <div className="flex items-center space-x-2">
                <FunnelIcon className="h-5 w-5 text-white/50" />
                <select
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                  className="bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {departments.map((department) => (
                    <option key={department.value} value={department.value} className="bg-slate-800">
                      {department.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Employees Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredEmployees.map((employee) => (
              <motion.div
                key={employee.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
                className="bg-white/10 backdrop-blur-lg rounded-3xl overflow-hidden border border-white/20 hover:border-blue-500/30 transition-all duration-300"
              >
                <div className="p-6">
                  <div className="flex justify-between items-start">
                    <div className="flex items-center">
                      {employee.photo ? (
                        <img 
                          src={employee.photo} 
                          alt={`${employee.firstName} ${employee.lastName}`} 
                          className="h-16 w-16 rounded-full object-cover border-2 border-blue-500/30" 
                        />
                      ) : (
                        <div className="h-16 w-16 rounded-full bg-gradient-to-br from-blue-500/30 to-purple-500/30 flex items-center justify-center">
                          <UserIcon className="h-8 w-8 text-white/70" />
                        </div>
                      )}
                      
                      <div className="ml-4">
                        <h3 className="text-lg font-medium text-white">
                          {employee.firstName} {employee.lastName}
                        </h3>
                        <p className="text-white/60 text-sm flex items-center">
                          {employee.position}
                          {employee.status === 'vacation' && (
                            <span className="ml-2 px-2 py-0.5 bg-amber-500/20 text-amber-300 text-xs rounded-full">
                              En congé
                            </span>
                          )}
                        </p>
                        <p className="text-white/40 text-xs mt-1">
                          {departments.find(d => d.value === employee.department)?.label}
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-1">
                      <button 
                        onClick={() => editEmployee(employee)}
                        className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                      >
                        <PencilIcon className="h-4 w-4 text-white/70" />
                      </button>
                      <button 
                        onClick={() => openDeleteModal(employee)}
                        className="p-1.5 rounded-lg bg-white/5 hover:bg-red-500/20 transition-colors"
                      >
                        <TrashIcon className="h-4 w-4 text-white/70 hover:text-red-400" />
                      </button>
                    </div>
                  </div>

                  <div className="mt-6 grid grid-cols-1 gap-3">
                    <div className="flex items-center">
                      <EnvelopeIcon className="h-4 w-4 text-blue-400" />
                      <span className="ml-2 text-white/70 text-sm">{employee.email}</span>
                    </div>
                    <div className="flex items-center">
                      <PhoneIcon className="h-4 w-4 text-green-400" />
                      <span className="ml-2 text-white/70 text-sm">{employee.phone}</span>
                    </div>
                    <div className="flex items-center">
                      <BanknotesIcon className="h-4 w-4 text-amber-400" />
                      <span className="ml-2 text-white/70 text-sm">{employee.salary.toLocaleString('fr-DZ')} DA/mois</span>
                    </div>
                  </div>

                  <div className="mt-4 flex justify-end">
                    <button 
                      onClick={() => viewEmployee(employee)}
                      className="px-3 py-1 text-sm text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      Voir détails
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {filteredEmployees.length === 0 && (
            <div className="flex flex-col items-center justify-center p-12 text-center bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20">
              <UserGroupIcon className="h-16 w-16 text-white/30 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">Aucun employé ne correspond à votre recherche</h3>
              <p className="text-white/60 mb-6">Essayez de modifier vos critères de filtrage</p>
              <button 
                onClick={() => {
                  setSearchTerm('');
                  setDepartmentFilter('all');
                }}
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 flex items-center space-x-2"
              >
                <ChartBarIcon className="h-5 w-5" />
                <span>Réinitialiser les filtres</span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Onglet de présence */}
      {activeTab === 'attendance' && (
        <AttendanceTracker />
      )}

      {/* Onglet de planning - À développer */}
      {activeTab === 'schedule' && (
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20 text-center">
          <CalendarDaysIcon className="h-16 w-16 text-white/30 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">
            Planning en développement
          </h3>
          <p className="text-white/60 max-w-md mx-auto">
            Cette fonctionnalité sera bientôt disponible pour vous permettre de gérer les horaires de travail de vos employés.
          </p>
        </div>
      )}

      {/* Onglet de rôles et accès - À développer */}
      {activeTab === 'roles' && (
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20 text-center">
          <IdentificationIcon className="h-16 w-16 text-white/30 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">
            Gestion des rôles en développement
          </h3>
          <p className="text-white/60 max-w-md mx-auto">
            Cette fonctionnalité sera bientôt disponible pour vous permettre de gérer les droits d'accès et les permissions de vos employés.
          </p>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && employeeToDelete && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl p-6 max-w-md w-full">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mb-4">
                <TrashIcon className="h-8 w-8 text-red-500" />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">Supprimer cet employé</h3>
              <p className="text-white/70 mb-6">
                Êtes-vous sûr de vouloir supprimer l'employé {employeeToDelete.firstName} {employeeToDelete.lastName} ? Cette action est irréversible.
              </p>
              <div className="flex gap-3 w-full">
                <button 
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="flex-1 px-4 py-2 border border-white/20 text-white/70 rounded-lg hover:bg-white/10"
                >
                  Annuler
                </button>
                <button 
                  onClick={() => handleDeleteEmployee(employeeToDelete.id)}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Employee Form Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-white">
                {selectedEmployee ? 'Modifier l\'employé' : 'Ajouter un employé'}
              </h2>
              <button 
                onClick={() => setShowAddForm(false)}
                className="p-1 rounded-full hover:bg-white/10"
              >
                <XMarkIcon className="h-6 w-6 text-white/70" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Formulaire à implémenter */}
              <p className="text-white/70">Formulaire de gestion des employés à implémenter</p>
              
              <div className="flex justify-end mt-6 gap-2">
                <button 
                  onClick={() => setShowAddForm(false)}
                  className="px-4 py-2 border border-white/20 text-white/70 rounded-lg hover:bg-white/10"
                >
                  Annuler
                </button>
                <button 
                  onClick={() => handleSaveEmployee({})}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Enregistrer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Employee Detail Modal */}
      {selectedEmployee && !showAddForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-white">
                Détails de l'employé
              </h2>
              <button 
                onClick={() => setSelectedEmployee(null)}
                className="p-1 rounded-full hover:bg-white/10"
              >
                <XMarkIcon className="h-6 w-6 text-white/70" />
              </button>
            </div>
            
            <div className="flex items-center gap-4 mb-6">
              {selectedEmployee.photo ? (
                <img 
                  src={selectedEmployee.photo} 
                  alt={`${selectedEmployee.firstName} ${selectedEmployee.lastName}`} 
                  className="h-24 w-24 rounded-full object-cover border-2 border-blue-500/30" 
                />
              ) : (
                <div className="h-24 w-24 rounded-full bg-gradient-to-br from-blue-500/30 to-purple-500/30 flex items-center justify-center">
                  <UserIcon className="h-12 w-12 text-white/70" />
                </div>
              )}
              
              <div>
                <h3 className="text-xl font-medium text-white">
                  {selectedEmployee.firstName} {selectedEmployee.lastName}
                </h3>
                <div className="flex items-center">
                  <p className="text-white/60">
                    {selectedEmployee.position}
                  </p>
                  {selectedEmployee.status === 'vacation' && (
                    <span className="ml-2 px-2 py-0.5 bg-amber-500/20 text-amber-300 text-xs rounded-full">
                      En congé
                    </span>
                  )}
                </div>
                <p className="text-white/50 text-sm">
                  {departments.find(d => d.value === selectedEmployee.department)?.label}
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-white/5 p-3 rounded-lg">
                <p className="text-white/50 text-sm mb-1">Email</p>
                <p className="text-white">{selectedEmployee.email}</p>
              </div>
              <div className="bg-white/5 p-3 rounded-lg">
                <p className="text-white/50 text-sm mb-1">Téléphone</p>
                <p className="text-white">{selectedEmployee.phone}</p>
              </div>
              <div className="bg-white/5 p-3 rounded-lg">
                <p className="text-white/50 text-sm mb-1">Date d'embauche</p>
                <p className="text-white">{new Date(selectedEmployee.joinDate).toLocaleDateString('fr-FR')}</p>
              </div>
              <div className="bg-white/5 p-3 rounded-lg">
                <p className="text-white/50 text-sm mb-1">Salaire mensuel</p>
                <p className="text-white">{selectedEmployee.salary.toLocaleString('fr-DZ')} DA</p>
              </div>
              <div className="bg-white/5 p-3 rounded-lg">
                <p className="text-white/50 text-sm mb-1">Sécurité sociale</p>
                <p className="text-white">{selectedEmployee.socialSecurity}</p>
              </div>
              <div className="bg-white/5 p-3 rounded-lg">
                <p className="text-white/50 text-sm mb-1">Horaires</p>
                <p className="text-white">{selectedEmployee.schedule}</p>
              </div>
            </div>
            
            <div className="bg-white/5 p-4 rounded-lg mb-6">
              <h4 className="text-lg font-medium text-white mb-2">Coordonnées complètes</h4>
              <div className="space-y-3">
                <div>
                  <p className="text-white/50 text-sm">Adresse</p>
                  <p className="text-white/70">{selectedEmployee.address}</p>
                </div>
                <div>
                  <p className="text-white/50 text-sm">Contact d'urgence</p>
                  <p className="text-white/70">{selectedEmployee.emergencyContact}</p>
                </div>
                <div>
                  <p className="text-white/50 text-sm">Coordonnées bancaires</p>
                  <p className="text-white/70">{selectedEmployee.bankDetails}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white/5 p-4 rounded-lg mb-6">
              <h4 className="text-lg font-medium text-white mb-2">Qualifications</h4>
              <div className="space-y-3">
                <div>
                  <p className="text-white/50 text-sm">Formation</p>
                  <p className="text-white/70">{selectedEmployee.education}</p>
                </div>
                {selectedEmployee.certifications && selectedEmployee.certifications.length > 0 && (
                  <div>
                    <p className="text-white/50 text-sm">Certifications</p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {selectedEmployee.certifications.map((cert, index) => (
                        <span key={index} className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm">
                          {cert}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex gap-3 justify-end">
              <button 
                onClick={() => {
                  setSelectedEmployee(null);
                  editEmployee(selectedEmployee);
                }}
                className="px-4 py-2 border border-white/20 text-white/70 rounded-lg hover:bg-white/10 flex items-center gap-1"
              >
                <PencilIcon className="h-4 w-4" />
                <span>Modifier</span>
              </button>
              <button 
                onClick={() => {
                  setSelectedEmployee(null);
                  openDeleteModal(selectedEmployee);
                }}
                className="px-4 py-2 bg-red-600/30 text-red-300 rounded-lg hover:bg-red-600/50 flex items-center gap-1"
              >
                <TrashIcon className="h-4 w-4" />
                <span>Supprimer</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeesPage; 